import { Customer } from '../types/types'
export const calculateOptimizedRoute = async (
  customers: Customer[],
): Promise<{
  route: google.maps.DirectionsResult
  summary: { distance: string; duration: string }
}> => {
  return new Promise((resolve, reject) => {
    if (customers.length < 2) {
      reject(
        new Error('At least 2 customers are required to calculate a route'),
      )
      return
    }
    const directionsService = new google.maps.DirectionsService()
    // First customer is the origin, last is the destination
    const origin = customers[0].location
    const destination = customers[customers.length - 1].location
    // All customers in between are waypoints
    // If there are only 2 customers, waypoints will be an empty array
    const waypoints =
      customers.length > 2
        ? customers.slice(1, customers.length - 1).map((customer) => ({
            location: customer.location,
            stopover: true,
          }))
        : []
    // Add more detailed logging to help debug
    console.log('Route optimization request:', {
      origin,
      destination,
      waypoints,
      customersCount: customers.length,
    })
    directionsService.route(
      {
        origin,
        destination,
        waypoints,
        optimizeWaypoints: true, // This tells Google to optimize the route
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        console.log('Direction service response status:', status)
        if (status === google.maps.DirectionsStatus.OK && result) {
          try {
            // Calculate total time including time spent at each location
            let totalTimeInMinutes = 0
            let totalDistanceInMeters = 0
            if (result.routes.length > 0 && result.routes[0].legs) {
              result.routes[0].legs.forEach((leg, index) => {
                // Log each leg for debugging
                console.log(`Leg ${index}:`, {
                  duration: leg.duration?.value,
                  distance: leg.distance?.value,
                })
                totalTimeInMinutes += leg.duration?.value || 0
                totalDistanceInMeters += leg.distance?.value || 0
                // Add the time spent at this customer location
                // For the case with exactly 2 customers, we need to handle differently
                if (customers.length === 2) {
                  // For 2 customers, add time for both origin and destination
                  if (index === 0) {
                    totalTimeInMinutes +=
                      customers[0].timeOnSite * 60 +
                      customers[1].timeOnSite * 60
                  }
                } else {
                  // For more than 2 customers
                  if (index < result.routes[0].legs.length - 1) {
                    totalTimeInMinutes += customers[index].timeOnSite * 60 // Convert minutes to seconds
                  } else {
                    // For the last leg, add time for the destination
                    totalTimeInMinutes +=
                      customers[customers.length - 1].timeOnSite * 60
                  }
                }
              })
            }
            // Convert total time from seconds to hours and minutes
            const hours = Math.floor(totalTimeInMinutes / 3600)
            const minutes = Math.floor((totalTimeInMinutes % 3600) / 60)
            // Convert total distance from meters to kilometers
            const kilometers = (totalDistanceInMeters / 1000).toFixed(1)
            const summary = {
              distance: `${kilometers} km`,
              duration: `${hours}h ${minutes}m (including stops)`,
            }
            console.log('Route optimization successful:', summary)
            resolve({
              route: result,
              summary,
            })
          } catch (error) {
            console.error('Error processing directions result:', error)
            reject(
              new Error(`Error processing directions result: ${error.message}`),
            )
          }
        } else {
          console.error('Direction service error:', status)
          reject(new Error(`Directions request failed: ${status}`))
        }
      },
    )
  })
}
